import React from 'react';
import styled from '@emotion/styled';
import { colors, Colors } from '@modernhealth/vital-foundation';
import { BodyTextSmall, HeadingText30, HeadingText40 } from '@modernhealth/vital-components';
import { useTranslation } from 'react-i18next';
import isPropValid from '@emotion/is-prop-valid';

import { InternalLink } from 'src/shared/components/InternalLink';

import { useTranslatedLanguage } from '../pages/CirclesOnDemandOverviewPage';

const languageCards = [
    { languageCode: 'es', backgroundColor: 'orange', base: 55, hover: 65 },
    { languageCode: 'ja', backgroundColor: 'red', base: 75, hover: 85 },
    { languageCode: 'hi', backgroundColor: 'cyan', base: 65, hover: 75 },
] as const;

export const NonEnglishCirclesSection = () => {
    const { t } = useTranslation('circles');

    return (
        <section>
            <HeadingText40 marginBottom={24}>{t('sections.international.header')}</HeadingText40>
            <div
                css={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    gap: 16,
                    'h4:first-letter': {
                        textTransform: 'capitalize',
                    },
                }}
            >
                {languageCards.map(({ languageCode, base, hover, backgroundColor }) => {
                    return (
                        <LanguageMenuCard
                            key={`language-card-${languageCode}`}
                            backgroundColor={`${backgroundColor}${base}`}
                            backgroundColorOnHover={`${backgroundColor}${hover}`}
                            languageCode={languageCode}
                        />
                    );
                })}
            </div>
        </section>
    );
};

interface LanguageMenuCardProps {
    backgroundColor: Colors;
    backgroundColorOnHover: Colors;
}

const LanguageMenuCardContainer = styled(InternalLink, {
    shouldForwardProp: isPropValid,
})<LanguageMenuCardProps>(({ backgroundColor, backgroundColorOnHover }) => ({
    flex: 1,
    padding: 24,
    minWidth: 200,
    backgroundColor: colors[backgroundColor],
    color: colors.white,
    borderRadius: 6,
    textDecoration: 'none',
    '&:hover': {
        color: colors.white,
        backgroundColor: colors[backgroundColorOnHover],
    },
}));

export const LanguageMenuCard: React.FC<LanguageMenuCardProps & { languageCode: string }> = ({
    languageCode,
    ...props
}) => {
    const { t } = useTranslation('circles');
    const language = useTranslatedLanguage(languageCode, languageCode);

    return (
        <LanguageMenuCardContainer
            {...props}
            to={`/circles/language/${languageCode}`}
            aria-label={language}
        >
            <HeadingText30 fontColor="white">{language}</HeadingText30>
            <BodyTextSmall>{t(`sections.international.${languageCode}.description`)}</BodyTextSmall>
        </LanguageMenuCardContainer>
    );
};
